// Web onboarding — the backend calls. Every call is one the business app
// makes (feature_business_onboarding), direct to the domain service with the
// bearer, through the same-origin proxies (/api/profile → ihjizli-profile,
// /api/booking → ihjizli-booking, /api/erp → erp-core):
//
//   GET  /api/profile/v1/categories                 public — the business types
//        → {categories:[{id, name_ar, name_plural_ar?, sort_order, active, default_scheduling_mode, booking_shape, …}]}
//   GET  /api/profile/v1/business/slug-available?name=&city=&category=  public — the public-page handle
//        → {available:false, suggestions:[…]} (slug absent, name set → suggestions derived from the name)
//   POST /api/profile/v1/public/invites/lookup {token}  public — «who invited me»
//        → {business_name, role, status:'pending'} · 404 «الدعوة غير موجودة» · 429
//   POST /api/profile/v1/business/invites/claim      business audience — phone-based claim
//        → {memberships:[{business_id, role, name_ar, category_id, city, logo_url}]}
//   GET  /api/profile/v1/business/me/identity        business audience — the PERSON's name (one per business identity)
//        → 200 {full_name:'' | '…'} (never 404: no row = '')
//   PUT  /api/profile/v1/business/me/identity {full_name}  → trims, 1..80 runes → 200 {full_name:'<stored>'}
//        · 422 {errors:[{field:'full_name', code:'required'|'too_long', message}]}
//   POST /api/profile/v1/business {name, category, city, area?, contactPhone?, slug?}  business audience
//        → 201 Business {id, name_ar, category_id, city, slug, …} (200 when it already existed)
//        · 422 {errors:[{field:'name'|'category'|'city'|'contactPhone'|'slug', code, message}]}
//        · 400 category_unknown «التصنيف غير معروف»
//   PUT  /api/booking/v1/business/{id}/scheduling {mode, slot_step_min:30, allow_staff_pick:true, shape, …}  manager+
//        (data/shapes.ts schedulingBody: unit + check_in_min/check_out_min, table + sitting_min / deposit)
//   PUT  /api/booking/v1/business/{id}/hours {hours:[{weekday 0=Sun…6=Sat, openMin, closeMin}]}  manager+
//   POST /api/profile/v1/business/{id}/invites {phone, role}   owner — team invite (ADR-0016)
//        → 201 {id, phone, name, role, invite_link?} · 422 phone invalid
//   GET  /api/profile/v1/business/{id}/invites                 owner/manager — pending + claimed rows
//   GET  /api/profile/v1/business/{id}/branches                staff+ — {branches:[{id, name, is_main, status, …}]}
//   PATCH /api/profile/v1/business/{id}/branches/{branchId} {name}  owner — the first branch's name (ADR-0027)
//   GET  /api/erp/v1/erp/branches?refresh=1                    member — erp-core mirrors profile's branches and
//        provisions each new one a main till «الصندوق الرئيسي» and a main warehouse (Phase 1E R19)
//
// Cities come from GET /api/business/v1/config → content.cities
// ([{name, areas[]}], platform content key `cities`); the legal links from
// content.legal {terms_url, privacy_url}; the support WhatsApp from
// content.contact {support_whatsapp | support_phone}. The category list from
// profile, never a hard-coded list.

import { bookingApi, erpApi, isApiError, profileApi } from '@/lib/api';
import { sessionStore } from '@/lib/auth';
import type { AppConfig } from '@/lib/config';
import { normalizeIraqiPhone } from '@/lib/digits';
import { OWNER_NAME } from '../copy';
import type { SchedulingPut } from './shapes';

/** ADR-0023 A1 §1: the six booking shapes a category names (profile `booking_shape`). */
export type BookingShape = 'staff' | 'place' | 'business' | 'unit' | 'seat' | 'table';

export interface BusinessType {
  id: string;
  name_ar: string;
  name_plural_ar?: string;
  icon?: string;
  sort_order: number;
  active: boolean;
  default_scheduling_mode: string;
  /** The shape a NEW business of this type books on (profile categories, migration 0040). */
  booking_shape?: BookingShape;
}

export interface PublicInvite {
  business_name: string;
  role: string;
  status: string;
}

export interface ClaimedMembership {
  business_id: string;
  role: 'owner' | 'manager' | 'staff' | string;
  name_ar: string;
  category_id?: string;
  city?: string;
  logo_url?: string;
}

export interface CreatedBusiness {
  id: string;
  name_ar?: string;
  category_id?: string;
  city?: string;
  slug?: string;
}

export interface BusinessDraft {
  name: string;
  category: string;
  city: string;
  area: string;
  /** The business's public contact number as typed (07…); '' = the account's own. */
  phone: string;
}

export interface City {
  name: string;
  areas: string[];
}

/** One open day for booking (closed days are simply absent). 0 = Sunday … 6 = Saturday. */
export interface DayHours {
  weekday: number;
  openMin: number;
  closeMin: number;
}

export interface TeamInvite {
  id?: string;
  phone: string;
  name?: string;
  role: string;
  /** pending | claimed (profile's listInvites); absent right after creation = pending. */
  status?: string;
}

/** The ACTIVE business types in the platform's order (profile GET /v1/categories). */
export async function fetchBusinessTypes(): Promise<BusinessType[]> {
  const res = await profileApi.get<{ categories?: BusinessType[] }>('/v1/categories', undefined, { businessId: null, authRecovery: false });
  return (res?.categories ?? [])
    .filter((c) => c && c.active !== false && typeof c.id === 'string' && c.id)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/** content.cities from the platform config; [] when the platform has not set it. */
export function citiesFromConfig(config: AppConfig | undefined): City[] {
  const raw = config?.content?.cities;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is { name: string; areas?: unknown } => !!c && typeof (c as { name?: unknown }).name === 'string' && !!(c as { name: string }).name)
    .map((c) => ({ name: c.name, areas: Array.isArray(c.areas) ? c.areas.filter((a): a is string => typeof a === 'string' && !!a) : [] }));
}

export interface FooterLinks {
  terms?: string;
  privacy?: string;
  help?: string;
}

function urlOf(v: unknown): string | undefined {
  return typeof v === 'string' && /^https?:\/\//.test(v.trim()) ? v.trim() : undefined;
}

/** The footer's الشروط · الخصوصية · المساعدة — only the links the platform has set (rule 7: omit the rest). */
export function footerLinksFromConfig(config: AppConfig | undefined): FooterLinks {
  const content = (config?.content ?? {}) as Record<string, unknown>;
  const legal = (content.legal ?? {}) as Record<string, unknown>;
  const contact = (content.contact ?? {}) as Record<string, unknown>;
  const wa = typeof contact.support_whatsapp === 'string' && contact.support_whatsapp ? contact.support_whatsapp : contact.support_phone;
  const digits = typeof wa === 'string' ? wa.replace(/\D/g, '') : '';
  return {
    terms: urlOf(legal.terms_url),
    privacy: urlOf(legal.privacy_url),
    help: digits ? `https://wa.me/${digits}` : undefined,
  };
}

/** settings.allow_signups (platform switch); missing = open, like the business app's default. */
export function signupsAllowed(config: AppConfig | undefined): boolean {
  const s = config?.settings as { allow_signups?: unknown; allowSignups?: unknown } | null | undefined;
  const v = s?.allow_signups ?? s?.allowSignups;
  return v !== false;
}

/** The invite token out of whatever was pasted: the WhatsApp link (…/join/<token>) or the bare token. */
export function inviteTokenFrom(input: string): string | null {
  const m = /([0-9a-f]{32,128})/i.exec(input.trim());
  return m ? m[1].toLowerCase() : null;
}

export async function lookupInvite(token: string): Promise<PublicInvite> {
  return profileApi.post<PublicInvite>('/v1/public/invites/lookup', { token }, { businessId: null, authRecovery: false });
}

/** Materialise any pending invites for the signed-in phone → every membership the person now has. */
export async function claimInvites(): Promise<ClaimedMembership[]> {
  const res = await profileApi.post<{ memberships?: ClaimedMembership[] }>('/v1/business/invites/claim', undefined, { businessId: null });
  return Array.isArray(res?.memberships) ? res.memberships : [];
}

/** The public-page handle profile derives from the name (its first suggestion); null when it has none. */
export async function suggestSlug(name: string, city: string, category: string): Promise<string | null> {
  const res = await profileApi.get<{ available?: boolean; suggestions?: string[] }>(
    '/v1/business/slug-available',
    { name: name.trim(), city: city.trim() || undefined, category: category || undefined },
    { businessId: null, authRecovery: false },
  );
  const s = res?.suggestions?.find((x) => typeof x === 'string' && x);
  return s ?? null;
}

export async function createBusiness(d: BusinessDraft, slug: string | null): Promise<CreatedBusiness> {
  const body: Record<string, unknown> = { name: d.name.trim(), category: d.category, city: d.city.trim() };
  if (d.area.trim()) body.area = d.area.trim();
  const phone = d.phone.trim() ? normalizeIraqiPhone(d.phone) : null;
  if (phone) body.contactPhone = phone;
  if (slug) body.slug = slug;
  return profileApi.post<CreatedBusiness>('/v1/business', body, { businessId: null });
}

// ------------------------------------------------------------ the person --

/** The query key of the signed-in person's identity name (the screen, the owner gate and nextAfterAccount share it). */
export const IDENTITY_KEY = ['onboarding', 'identity'] as const;

/** profile's business_identities.full_name CHECK: 1..80 characters after trimming. */
export const OWNER_NAME_MAX = 80;

/**
 * The signed-in person's own name from their business identity ('' = none yet).
 * Person-level: no business needed, so it never carries X-Business-Id.
 */
export async function fetchIdentityName(): Promise<string> {
  const res = await profileApi.get<{ full_name?: unknown }>('/v1/business/me/identity', undefined, { businessId: null });
  return typeof res?.full_name === 'string' ? res.full_name.trim() : '';
}

/** Save the person's name; answers the name profile stored (trimmed). */
export async function saveIdentityName(fullName: string): Promise<string> {
  const name = fullName.trim();
  const res = await profileApi.put<{ full_name?: unknown }>('/v1/business/me/identity', { full_name: name }, { businessId: null });
  return typeof res?.full_name === 'string' && res.full_name.trim() ? res.full_name.trim() : name;
}

/** profile's 422 on `full_name` → its own Arabic sentence (null = not a field error). */
export function identityFieldError(err: unknown): string | null {
  if (!isApiError(err) || err.status !== 422) return null;
  const hit = (err.errors ?? []).find((e) => e.field === 'full_name');
  if (!hit) return null;
  return hit.message || (hit.code === 'too_long' ? OWNER_NAME.tooLong : OWNER_NAME.required);
}

/** The signed-in person's display name, or null when the session only knows the phone. */
export function sessionPersonName(): string | null {
  const user = sessionStore.getUser();
  const name = user?.name?.trim() ?? '';
  if (!name) return null;
  if (name === user?.phone || normalizeIraqiPhone(name) !== null) return null;
  return name;
}

/** The scheduling PUT with the shape (data/shapes.ts schedulingBody) — awaited, never best effort. */
export async function putScheduling(businessId: string, body: SchedulingPut): Promise<void> {
  await bookingApi.put(`/v1/business/${encodeURIComponent(businessId)}/scheduling`, body, { businessId });
}

export async function putHours(businessId: string, hours: DayHours[]): Promise<void> {
  if (hours.length === 0) return;
  await bookingApi.put(`/v1/business/${encodeURIComponent(businessId)}/hours`, { hours }, { businessId });
}

export async function createInvite(businessId: string, phone: string): Promise<TeamInvite> {
  const res = await profileApi.post<TeamInvite>(`/v1/business/${encodeURIComponent(businessId)}/invites`, { phone, role: 'staff' }, { businessId });
  return { id: res?.id, phone: res?.phone ?? phone, name: res?.name, role: res?.role ?? 'staff', status: res?.status ?? 'pending' };
}

export async function listInvites(businessId: string): Promise<TeamInvite[]> {
  const res = await profileApi.get<{ invites?: TeamInvite[] } | TeamInvite[]>(`/v1/business/${encodeURIComponent(businessId)}/invites`, undefined, { businessId });
  const rows = Array.isArray(res) ? res : (res?.invites ?? []);
  return rows.filter((r): r is TeamInvite => !!r && typeof r.phone === 'string');
}

/** One profile branch, as much of it as the wizard reads. */
export interface ProfileBranchRow {
  id: string;
  name: string;
  is_main: boolean;
  status?: string;
}

/** The business's branches in profile (its main branch is created with the business). */
export async function listBranches(businessId: string): Promise<ProfileBranchRow[]> {
  const res = await profileApi.get<{ branches?: ProfileBranchRow[] }>(`/v1/business/${encodeURIComponent(businessId)}/branches`, undefined, { businessId });
  return Array.isArray(res?.branches) ? res.branches.filter((b): b is ProfileBranchRow => !!b && typeof b.id === 'string') : [];
}

export async function patchBranch(businessId: string, branchId: string, body: { name: string }): Promise<void> {
  await profileApi.patch(`/v1/business/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}`, body, { businessId });
}

/**
 * erp-core reads profile's branches now (mirror + provisioning: the main till
 * «الصندوق الرئيسي» and the main warehouse of every new branch). Replaces the
 * done screen's POST /tills (ruling R19), so a new business never gets two.
 */
export async function erpRefresh(businessId: string): Promise<void> {
  await erpApi.get('/branches', { refresh: '1' }, { businessId });
}

/** profile's 422 field errors → {field: message}. */
export function fieldErrors(err: unknown): Partial<Record<keyof BusinessDraft | 'slug', string>> {
  if (!isApiError(err)) return {};
  const out: Partial<Record<keyof BusinessDraft | 'slug', string>> = {};
  for (const e of err.errors ?? []) {
    if (e.field === 'name' || e.field === 'category' || e.field === 'city' || e.field === 'area' || e.field === 'slug') out[e.field] = e.message ?? '';
    if (e.field === 'contactPhone') out.phone = e.message ?? '';
  }
  if (err.code === 'category_unknown') out.category = err.detail ?? 'التصنيف غير معروف';
  return out;
}

/** The reference the server-error card prints (RFC 9457 trace_id / request id), or null. */
export function errorRef(err: unknown): string | null {
  if (!isApiError(err)) return null;
  const x = err.extensions as Record<string, unknown>;
  const v = x.trace_id ?? x.request_id ?? x.requestId;
  return typeof v === 'string' && v ? v : null;
}

/** Any server answer = 'server' (5xx «الخلل عندنا», 4xx its own sentence — errorDetail); only no response (offline, timeout) = network. */
export function errorKind(err: unknown): 'server' | 'network' {
  return isApiError(err) && err.status > 0 ? 'server' : 'network';
}

/** A 4xx's own sentence (RFC 9457 detail) for the error card; null for 5xx / transport errors. */
export function errorDetail(err: unknown): string | null {
  return isApiError(err) && err.status >= 400 && err.status < 500 && err.detail ? err.detail : null;
}

/** The shell error card's fields for a failure. */
export function failureOf(err: unknown): { kind: 'server' | 'network'; ref: string | null; detail: string | null } {
  return { kind: errorKind(err), ref: errorRef(err), detail: errorDetail(err) };
}
